import React, { useEffect, useState, useRef } from 'react';
import { DraggableComponents } from '../../../../../common/types/checkout';
import * as S from './styles';

type Props = {
  component: DraggableComponents;
};

export const InstagramSocialProof: React.FC<Props> = ({ component }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const videos = component?.configurations?.testimonial?.instagram ?? [];
  const current = videos[currentVideoIndex];

  const videoSrc = current?.videoUrl;
  const avatarSrc = current?.avatar;

  const togglePlayPause = () => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;
    if (v.paused) {
      v.play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          return;
        });
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleLike = () => {
    setIsLiked(true);
    const storyElement = document.getElementById('instagram-story');
    if (!storyElement) return;
    const rect = storyElement.getBoundingClientRect();
    for (let i = 0; i < 8; i++) {
      setTimeout(() => {
        const heart = document.createElement('div');
        heart.className = 'floating-heart';
        heart.innerHTML = '❤️';
        heart.style.left =
          rect.width * 0.3 + Math.random() * rect.width * 0.4 + 'px';
        heart.style.fontSize = 20 + Math.random() * 15 + 'px';
        heart.style.animationDelay = '0s';
        heart.style.animationDuration = 2 + Math.random() + 's';
        storyElement.appendChild(heart);
        setTimeout(() => heart.remove(), 3000);
      }, i * 100);
    }
  };

  const handleVideoEnd = () => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
      setProgress(0);
    } else {
      setCurrentVideoIndex(0);
      setProgress(0);
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    const updateProgress = () => {
      if (v?.duration) setProgress((v.currentTime / v.duration) * 100);
    };
    v?.addEventListener('timeupdate', updateProgress);
    return () => v?.removeEventListener('timeupdate', updateProgress);
  }, [currentVideoIndex, videoSrc]);

  useEffect(() => {
    setProgress(0);
    setIsPlaying(true);
    const v = videoRef.current;
    if (v) {
      v.currentTime = 0;
      if (videoSrc) v.play().catch(() => setIsPlaying(false));
    }
  }, [currentVideoIndex, videoSrc]);

  if (!videos.length) {
    return (
      <div style={{ color: '#999' }}>Adicione um depoimento do Instagram.</div>
    );
  }

  return (
    <S.VideoStoriesContainer>
      <S.InstagramStory id="instagram-story">
        <S.StoryProgressBar>
          {videos.map((_, index) => (
            <S.ProgressSegmentContainer key={index}>
              <S.ProgressSegmentBg />
              <S.ProgressSegmentFill
                style={{
                  width:
                    index < currentVideoIndex
                      ? '100%'
                      : index === currentVideoIndex
                        ? `${progress}%`
                        : '0%',
                }}
              />
            </S.ProgressSegmentContainer>
          ))}
        </S.StoryProgressBar>

        <S.StoryHeader>
          <S.StoryHeaderLeft>
            <S.Avatar
              src={avatarSrc}
              alt={current?.name}
              style={{
                width: '32px',
                height: '32px',
                border: '2px solid white',
              }}
            />
            <S.StoryUserInfo>
              <S.TestimonialName
                style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}
              >
                {current?.name}
              </S.TestimonialName>
              <S.StoryTime>{current?.time}</S.StoryTime>
            </S.StoryUserInfo>
          </S.StoryHeaderLeft>

          <S.StoryHeaderRight>
            <S.StoryControlButton
              onClick={togglePlayPause}
              disabled={!videoSrc}
            >
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <rect x="6" y="4" width="4" height="16"></rect>
                  <rect x="14" y="4" width="4" height="16"></rect>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </S.StoryControlButton>

            <S.StoryControlButton onClick={toggleMute}>
              {isMuted ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <line x1="23" y1="9" x2="17" y2="15"></line>
                  <line x1="17" y1="9" x2="23" y2="15"></line>
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                >
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                </svg>
              )}
            </S.StoryControlButton>

            <S.StoryControlButton>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <circle cx="12" cy="5" r="2"></circle>
                <circle cx="12" cy="12" r="2"></circle>
                <circle cx="12" cy="19" r="2"></circle>
              </svg>
            </S.StoryControlButton>
          </S.StoryHeaderRight>
        </S.StoryHeader>

        <S.StoryVideo
          ref={videoRef}
          src={videoSrc}
          autoPlay
          loop={false}
          muted={isMuted}
          playsInline
          onEnded={handleVideoEnd}
        />

        <S.StoryActions>
          <S.InstagramActionButton onClick={handleLike} liked={isLiked}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill={isLiked ? '#ff3b5c' : 'none'}
              stroke="white"
              strokeWidth="2"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </S.InstagramActionButton>
          <S.InstagramActionButton>
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
            >
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </S.InstagramActionButton>
        </S.StoryActions>
      </S.InstagramStory>
    </S.VideoStoriesContainer>
  );
};
